import React, { useEffect, useState, useContext, useMemo } from "react";
import { Modal, Button, Radio, message, Divider, Typography, Row, Col, Card, Tag, DatePicker, TimePicker, Input, Space, Empty, Checkbox } from "antd";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { CartContext } from "../CartContext";
import DeliveryAddressServ from "../service/DeliveryAddressesServ";
import OrdersServ from "../service/OrdersServ";
import PaymentServ from "../service/PaymentServ";
import DeliveryAddressModal from "./DeliveryAddressModel";
import SafeImage from "./SafeImage";
import {
    IconChevronRight,
    IconCheck,
    IconCalendar,
    IconClock,
    IconCreditCard,
    IconTruck,
    IconBuildingBank,
    IconQrcode,
    IconChevronLeft,
    IconMapPin,
    IconPackage,
    IconCreditCardPay,
    IconPlus,
    IconSmartHome,
    IconBuildingStore,
    IconEdit,
    IconTrash,
    IconMinus,
    IconTag,
    IconSun,
    IconCloud,
    IconMoon
} from "@tabler/icons-react";
import dayjs from "dayjs";
import UserServ from "../service/UserServ";
import NotificationService from "../service/NotificationService";
import ShopServ from "../service/ShopServ";
import CouponServ from "../service/CouponServ";
import OfferServ from "../service/OfferServ";
import { formatUnit } from "../util/unitFormatter";
import { useDeliveryEstimate } from "../hooks/useDeliveryEstimate";
import { LocationContext } from "../LocationContext";
import "../css/CheckoutPage.css";

const { Title, Text, Paragraph } = Typography;

const CheckoutPageContent = ({ items = null, open, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { cart, refreshCart, updateQuantity, removeFromCart } = useContext(CartContext);
    const [addressList, setAddressList] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [prevAddressId, setPrevAddressId] = useState(null);
    const [deliveryFeeUpdated, setDeliveryFeeUpdated] = useState(false);


    const [showAddressGrid, setShowAddressGrid] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("ONLINE");
    const [paymentDetails, setPaymentDetails] = useState({ upiId: "", cardNumber: "", expiry: "", cvv: "" });
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    // const [scheduledDate, setScheduledDate] = useState(null); // Deprecated for multi-shop
    // const [scheduledTime, setScheduledTime] = useState(null); // Deprecated

    const [addressAddModalOpen, setAddressAddModalOpen] = useState(false);
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [editingAddressDetails, setEditingAddressDetails] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [useCredits, setUseCredits] = useState(false);
    const [creditsToUse, setCreditsToUse] = useState(0);
    const [confirmCreditModal, setConfirmCreditModal] = useState(false);
    const [couponCode, setCouponCode] = useState("");
    const [couponDiscount, setCouponDiscount] = useState(0);
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [appliedCouponDetails, setAppliedCouponDetails] = useState(null);
    const [applyingCoupon, setApplyingCoupon] = useState(false);
    const [couponError, setCouponError] = useState("");
    const [availableCoupons, setAvailableCoupons] = useState([]);
    const [availableOffers, setAvailableOffers] = useState([]);
    const DEFAULT_FEE = Number(process.env.REACT_APP_DEFAULT_DELIVERY_FEE) || 50;
    const CUSTOM_CHARGE = Number(process.env.REACT_APP_CUSTOM_DELIVERY_CHARGE) || 50;

    // We no longer manage deliveryFee exclusively via state here, we derive it from CartContext
    // const [deliveryFee, setDeliveryFee] = useState(0);
    const [scheduledDate, setScheduledDate] = useState(dayjs());
    const [selectedSlot, setSelectedSlot] = useState('Normal');
    const [customTime, setCustomTime] = useState(null);
    const [shopsData, setShopsData] = useState({});
    const { latitude, longitude, addressName } = useContext(LocationContext);
    const [shopIds, setShopIds] = useState([]);


    const getDeliveryType = () => {
        if (!selectedSlot || selectedSlot === 'Normal') return 'Normal';
        if (selectedSlot === 'custom') return 'Custom';
        return 'Scheduled';
    };

    const deliveryType = getDeliveryType();



    const { estimates, formatETA, loading: estLoading, summary } = useDeliveryEstimate(shopIds, latitude, longitude, selectedAddressId, deliveryType);
    const [modalWidth, setModalWidth] = useState(window.innerWidth > 1024 ? 900 : (window.innerWidth > 768 ? '85%' : '95%'));
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
    const topRef = React.useRef(null);

    const loadRazorpay = () => {
        return new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => {
                resolve(true);
            };
            script.onerror = () => {
                resolve(false);
            };
            document.body.appendChild(script);
        });
    };

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            setModalWidth(width > 1024 ? 900 : (width > 768 ? '85%' : '95%'));
            setIsMobile(width <= 992);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);



    // 🔍 Helper to resolve price and MRP for variants/options
    const resolvePriceAndMrp = (item) => {
        // If the item already has a resolved price from the backend/cart context, use it
        if (item.price !== undefined) {
            return { price: item.price, mrp: item.mrp || item.price };
        }

        const product = item.product || item;
        let price = product?.price || 0;
        let mrp = product?.mrp || 0;

        // Check new options structure first
        if (product?.options && product.options.length > 0 && item.selectedOptions) {
            // Find the matching variant from options
            for (const optGroup of product.options) {
                const selectedValue = item.selectedOptions?.[optGroup.name];
                if (selectedValue && optGroup.variants) {
                    const variant = optGroup.variants.find(v => v.value === selectedValue);
                    if (variant) {
                        price = variant.price;
                        mrp = variant.mrp || product?.mrp || 0;
                        break;
                    }
                }
            }
        }
        // Fallback to old hasVariants structure
        else if (product?.hasVariants && product?.variants && item.selectedOptions) {
            const type = product.variantType;
            const selectedValue = item.selectedOptions?.[type];
            if (selectedValue) {
                const variant = product.variants.find(v => v.value === selectedValue);
                if (variant) {
                    price = variant.price;
                    mrp = variant.mrp || product?.mrp || 0;
                }
            }
        }

        return { price, mrp };
    };

    // Determine checkout items
    const checkoutItems = items || cart?.items || [];

    // Calculate totals including MRP for savings display
    const totals = checkoutItems.reduce((acc, item) => {
        const { price, mrp } = resolvePriceAndMrp(item);
        const qty = item.quantity || 1;
        return {
            subtotal: acc.subtotal + (price * qty),
            totalMrp: acc.totalMrp + ((mrp || price) * qty),
            totalQty: acc.totalQty + qty
        };
    }, { subtotal: 0, totalMrp: 0, totalQty: 0 });

    const subtotal = totals.subtotal;
    const totalMrp = totals.totalMrp;
    const itemSavings = Math.max(0, totalMrp - subtotal);

    const billDetails = cart?.billDetails || {};
    const handlingFee = billDetails.handlingFee || 0;

    // Pull the true delivery fee from the useDeliveryEstimate hook which natively factors in 
    // the currently selected address and super admin delivery rules (e.g. 5km radius, Rs 20 base fee).
    const baseDeliveryFee = (deliveryType === 'Scheduled' && selectedSlot !== 'custom') ? 0 : (summary?.totalDeliveryFee ?? (() => {
        const fee = billDetails.deliveryFee ?? DEFAULT_FEE;
        const hasCustom = selectedSlot === 'custom';
        return hasCustom ? fee + CUSTOM_CHARGE : fee;
    })());

    const deliveryFee = Math.max(0, baseDeliveryFee - (appliedCouponDetails?.deliveryDiscount || 0));


    const totalAmount = subtotal + deliveryFee + handlingFee;
    const finalAmount = Math.max(0, totalAmount - (useCredits ? creditsToUse : 0) - couponDiscount);
    const totalSavings = itemSavings + couponDiscount + (appliedCouponDetails?.deliveryDiscount || 0) + (useCredits ? creditsToUse : 0);

    // Keep credits to use in sync with total available after coupon
    useEffect(() => {
        if (useCredits) {
            const maxAvailable = userProfile?.credits || 0;
            const remainingPayable = Math.max(0, totalAmount - couponDiscount);
            setCreditsToUse(Math.min(maxAvailable, remainingPayable));
        } else {
            setCreditsToUse(0);
        }
    }, [useCredits, totalAmount, couponDiscount, userProfile?.credits]);

    const selectedAddress = addressList.find(a => (a.id || a._id) === selectedAddressId);

    // Show delivery fee update banner when address changes (not on first load)
    useEffect(() => {
        if (prevAddressId !== null && selectedAddressId !== null && prevAddressId !== selectedAddressId) {
            setDeliveryFeeUpdated(true);
            const timer = setTimeout(() => setDeliveryFeeUpdated(false), 5000);
            setPrevAddressId(selectedAddressId); // update for next change
            return () => clearTimeout(timer);
        }
        if (selectedAddressId !== null) {
            setPrevAddressId(selectedAddressId);
        }
    }, [selectedAddressId]);

    const loadAvailableCouponsAndOffers = async () => {
        try {
            const res = await CouponServ.getMyCoupons();
            setAvailableCoupons(res.data || []);

            const user = JSON.parse(localStorage.getItem("user"));
            if (user && user._id) {
                const offRes = await OfferServ.getByUser(user._id);
                setAvailableOffers(offRes.data?.docs || offRes.data || offRes || []);
            }
        } catch (error) {
            console.error("Failed to load coupons/offers", error);
        }
    };

    const shopIdsStr = useMemo(() => {
        return [...new Set(checkoutItems.map(item => {
            const p = item.product || item;
            return p?.shopId?._id || p?.shopId;
        }))].filter(Boolean).join(',');
    }, [checkoutItems]);

    useEffect(() => {
        if (checkoutItems.length > 0) {
            const ids = shopIdsStr ? shopIdsStr.split(',') : [];

            setShopIds(prev => {
                if (JSON.stringify(prev) === JSON.stringify(ids)) return prev;
                return ids;
            });

            if (ids.length > 0) {
                fetchShopsData(ids);
            }
            loadAvailableCouponsAndOffers();
        }
    }, [shopIdsStr, checkoutItems.length]);

    const fetchShopsData = async (ids) => {
        try {
            const promises = ids.map(id => ShopServ.single(id));
            const responses = await Promise.all(promises);
            const newData = {};
            responses.forEach(res => {
                if (res?.data) newData[res.data._id || res.data.id] = res.data;
            });
            setShopsData(newData);
        } catch (e) {
            console.error("Shop fetch error", e);
        }
    };

    // Auto-select slot - Only if not already set (e.g. first load)
    useEffect(() => {
        if (shopIds.length > 0) {
            const now = dayjs();
            if (!customTime) {
                setCustomTime(now);
            }
        }
    }, [shopIds]);


    // We removed the frontend-only delivery fee override use-effect because
    // the backend calculates deliveryFee based on Super Admin settings and location.
    // The deliveryFee is now a derived constant based on `billDetails.deliveryFee`.

    // Simple Global Schedule Synchronization
    const updateGlobalDate = (date) => {
        setScheduledDate(date);
    };

    const updateGlobalSlot = (slotVal) => {
        setSelectedSlot(slotVal);
    };

    const updateGlobalCustomTime = (time) => {
        setCustomTime(time);
    };





    const loadAddress = async () => {
        try {
            const res = await DeliveryAddressServ.index();
            // Handle both { data: [...] } and directly [...] formats
            const addresses = Array.isArray(res) ? res : (res?.data || []);

            setAddressList(addresses);

            if (addresses.length > 0) {
                if (!selectedAddressId) {
                    const defaultAddr = addresses.find(a => a.isDefault) || addresses[0];
                    setSelectedAddressId(defaultAddr.id || defaultAddr._id);
                }
            } else {
                // If address list is empty from specialized service, try fallback from profile
                const profileRes = await UserServ.getProfile();
                const profileAddr = profileRes?.data?.address || [];
                if (profileAddr.length > 0) {
                    setAddressList(profileAddr);
                    if (!selectedAddressId) {
                        const def = profileAddr.find(a => a.isDefault) || profileAddr[0];
                        setSelectedAddressId(def.id || def._id);
                    }
                }
            }
        } catch (error) {
            console.error("Failed to load addresses", error);
            // message.error("Failed to load addresses");
        }
    };

    useEffect(() => {
        loadAddress();
        loadProfile();
        setShowAddressGrid(false);
        setUseCredits(false);
        setCreditsToUse(0);
    }, []);

    const loadProfile = async () => {
        try {
            const res = await UserServ.getProfile();
            setUserProfile(res.data);
        } catch (error) {
            console.error("Error loading profile:", error);
        }
    };

    const handleUseCreditsToggle = (checked) => {
        if (checked) {
            setConfirmCreditModal(true);
        } else {
            setUseCredits(false);
            setCreditsToUse(0);
        }
    };

    const handleApplyCoupon = async (codeOverride = null) => {
        const codeToApply = (codeOverride && typeof codeOverride === 'string') ? codeOverride : couponCode;
        if (!codeToApply) return;
        try {
            setCouponError("");
            setApplyingCoupon(true);

            // 1. Check if the code is actually a Native App Offer
            const matchedOffer = availableOffers.find(o => (o.code || "").toUpperCase() === codeToApply.toUpperCase() && o.isActive !== false);
            if (matchedOffer) {
                // Support both API shapes: {discountValue, discountType} and legacy {discount, isPercentage}
                const dVal = matchedOffer.discountValue ?? matchedOffer.discount ?? 0;
                const dType = matchedOffer.discountType ?? (matchedOffer.isPercentage ? 'percentage' : 'amount');
                const dValNum = parseFloat(dVal) || 0;
                const discountValue = dType === 'percentage'
                    ? (subtotal * dValNum) / 100
                    : Math.min(dValNum, subtotal);

                setCouponCode(matchedOffer.code);
                setCouponDiscount(Math.round(discountValue));
                setAppliedCoupon(matchedOffer.code);
                setAppliedCouponDetails({
                    ...matchedOffer,
                    discountType: dType,
                    discountValue: dValNum
                });
                message.success(`Offer "${matchedOffer.code}" applied! You saved ₹${Math.round(discountValue)}`);
                setApplyingCoupon(false);
                return;
            }

            // 2. Fallback to Strict Coupons via backend Database
            const res = await CouponServ.apply({ code: codeToApply, cartTotal: subtotal });
            setCouponCode(res.data.couponCode); // Ensure input reflects correctly
            setCouponDiscount(res.data.discount);
            setAppliedCoupon(res.data.couponCode);
            setAppliedCouponDetails(res.data);
            message.success(`Coupon "${res.data.couponCode}" applied! You saved ₹${res.data.discount}`);
        } catch (err) {
            const errorMsg = err.message || "Failed to apply coupon";
            setCouponError(errorMsg);
            message.error(errorMsg);
            setCouponDiscount(0);
            setAppliedCoupon(null);
            setAppliedCouponDetails(null);
        } finally {
            setApplyingCoupon(false);
        }
    };

    // Auto-apply coupon from Offers page or LocalStorage
    useEffect(() => {
        const pendingCode = location.state?.couponCode || localStorage.getItem('pendingCoupon');
        if (pendingCode) {
            if (subtotal > 0 && !appliedCoupon && !applyingCoupon) {
                handleApplyCoupon(pendingCode);
                localStorage.removeItem('pendingCoupon');
                if (location.state?.couponCode) {
                    navigate('.', { replace: true, state: {} });
                }
            } else if (subtotal === 0 && pendingCode !== couponCode) {
                setCouponCode(pendingCode);
                localStorage.removeItem('pendingCoupon');
                if (location.state?.couponCode) {
                    navigate('.', { replace: true, state: {} });
                }
            }
        }
    }, [location.state?.couponCode, subtotal, appliedCoupon, applyingCoupon, couponCode, navigate]);

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setAppliedCouponDetails(null);
        setCouponDiscount(0);
        setCouponCode("");
        setCouponError("");
        message.info("Coupon removed");
    };

    const handleConfirmCredits = () => {
        const available = userProfile?.credits || 0;
        const toUse = Math.min(available, totalAmount);
        setCreditsToUse(toUse);
        setUseCredits(true);
        setConfirmCreditModal(false);
        message.success(`₹${toUse} credits will be applied!`);
    };

    const handleEditAddress = (addr) => {
        setEditingAddressDetails(addr);
        setIsEditingAddress(true);
        setAddressAddModalOpen(true);
    };

    const handleDeleteAddress = async (id) => {
        try {
            await DeliveryAddressServ.remove(id);
            message.success("Address removed successfully");
            loadAddress();
            if (selectedAddressId === id) {
                setSelectedAddressId(null);
            }
        } catch (error) {
            message.error("Failed to remove address");
        }
    };

    const handlePlaceOrder = async (finalMethod = null) => {

        const activeMethod = finalMethod || paymentMethod;
        const onlineMethods = ['ONLINE', 'UPI', 'CARD', 'NETBANKING'];

        console.log("handlePlaceOrder triggered with method:", activeMethod);
        console.log("Current state:", { selectedAddressId, shopIds: shopIds.length, finalAmount, submitting });

        if (submitting) {
            console.log("Already submitting, ignoring click.");
            return;
        }

        if (!selectedAddressId) {
            console.log("Aborting: No address selected");
            message.warning("Please select a delivery address");
            return;
        }


        // Validate Slots (Single Global Check)
        let valid = true;
        const slotsToCheck = { '09:00 - 12:00': 12, '13:00 - 16:00': 16, '18:00 - 21:00': 21 };

        console.log(`Checking slots:`, { scheduledDate, selectedSlot });

        if (!scheduledDate || !selectedSlot) {
            console.log(`Validation Failed: Missing date or slot`);
            message.warning("Please select a delivery date and time");
            valid = false;
        } else if (dayjs(scheduledDate).isBefore(dayjs().startOf('day'))) {
            message.error("Scheduled date cannot be in the past.");
            valid = false;
        } else if (dayjs(scheduledDate).isSame(dayjs(), 'day')) {
            if (selectedSlot !== 'custom') {
                if (dayjs().hour() >= slotsToCheck[selectedSlot]) {
                    message.error(`Time slot ${selectedSlot} has passed for today.`);
                    valid = false;
                }
            } else if (customTime) {
                const selectedTime = dayjs(scheduledDate).hour(dayjs(customTime).hour()).minute(dayjs(customTime).minute());
                if (selectedTime.isBefore(dayjs().add(5, 'minute'))) {
                    message.error("Custom time must be at least 5 minutes from now.");
                    valid = false;
                }
            }
        }

        if (!valid) {
            console.log("Validation failed. Check warnings in UI.");
            return;
        }

        console.log("Validation successful. Proceeding with order placement.");
        setSubmitting(true);
        console.log("Preparing payload for method:", activeMethod);


        const shopSchedules = {};
        shopIds.forEach(id => {
            const sId = id.toString();
            shopSchedules[sId] = {
                scheduledDate: scheduledDate ? scheduledDate.toISOString() : null,
                scheduledTime: selectedSlot === 'custom'
                    ? (customTime ? customTime.format("HH:mm") : null)
                    : selectedSlot
            };
        });


        const payload = {
            deliveryAddressId: selectedAddressId,
            paymentMethod: activeMethod,
            paymentType: activeMethod,
            paymentDetails: paymentDetails,
            items: items ? items.map(item => {
                const productId = item.product?._id || item._id || item.id;
                return {
                    product: productId,
                    quantity: item.quantity || 1,
                    selectedOptions: item.selectedOptions || {}
                };
            }) : null,
            useCredits: useCredits,
            totalCreditsToUse: creditsToUse,
            shopSchedules: shopSchedules, // New Multi-shop payload
            // scheduledDate/Time: Fallback global values
            scheduledDate: scheduledDate?.toISOString(),
            scheduledTime: selectedSlot || "Normal",

            deliveryFee: deliveryFee,
            handlingFee: handlingFee,
            couponCode: appliedCoupon,
            deliveryLocation: (latitude && longitude) ? {
                lat: latitude,
                lng: longitude
            } : null
        };


        try {
            if (isNaN(finalAmount) || finalAmount < 0) {
                console.error("Invalid finalAmount detected:", finalAmount);
                message.error("Invalid total amount. Please refresh and try again.");
                setSubmitting(false);
                return;
            }

            let orderIds = [];
            let createdOrders = [];
            let rzpOrder = null;
            let transactionId = null;

            try {
                console.log("Calling OrdersServ.placeOrder with payload:", payload);
                const res = await OrdersServ.placeOrder(payload);
                if (res && res.isOnline) {
                    rzpOrder = res.rzpOrder;
                    transactionId = res.transactionId;
                } else {
                    createdOrders = Array.isArray(res) ? res : (res?.data || []);
                    orderIds = createdOrders.map(o => o._id);
                }
            } catch (placeErr) {
                console.error("Order Placement Error:", placeErr);
                message.error(placeErr.message || "Failed to place order");
                setSubmitting(false);
                return;
            }

            if (onlineMethods.includes(activeMethod) && Math.round(finalAmount) > 0) {
                console.log("Initiating online payment flow...");
                if (typeof window.Razorpay === 'undefined') {
                    console.log("Razorpay SDK missing on window. Loading...");
                    const res = await loadRazorpay();
                    if (!res) {
                        console.error("Failed to load Razorpay script.");
                        message.error("Razorpay SDK failed to load. Are you online?");
                        if (transactionId) {
                            try { await OrdersServ.cancelPending({ transactionId }); } catch (e) {}
                        }
                        setSubmitting(false);
                        return;
                    }
                }
                console.log("Razorpay SDK ready.");

                const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
                if (!razorpayKey) {
                    console.error("Razorpay key is missing. Set REACT_APP_RAZORPAY_KEY_ID in your web app environment.");
                    message.error("Payment cannot proceed because Razorpay is not configured. Please contact support.");
                    if (transactionId) {
                        try { await OrdersServ.cancelPending({ transactionId }); } catch (e) {}
                    }
                    setSubmitting(false);
                    return;
                }

                let isPaymentSuccessful = false;
                const options = {
                    key: razorpayKey,
                    amount: rzpOrder.amount,
                    currency: rzpOrder.currency,
                    name: "Nearzon",
                    description: "Order Payment",
                    order_id: rzpOrder.id,
                    handler: async function (response) {
                        setSubmitting(true);
                        try {
                            const verifyRes = await PaymentServ.verifyPayment({
                                razorpayOrderId: response.razorpay_order_id,
                                razorpayPaymentId: response.razorpay_payment_id,
                                razorpaySignature: response.razorpay_signature,
                            });
                            if (verifyRes.success) {
                                isPaymentSuccessful = true;
                                message.success("Payment Successful!");
                                const finalizedOrders = verifyRes.orders || [];
                                finalizeOrder(finalizedOrders);
                            } else {
                                message.error("Payment Verification Failed!");
                                if (transactionId) {
                                    try {
                                        await OrdersServ.cancelPending({ transactionId });
                                    } catch (cancelErr) {
                                        console.error("Error cancelling pending transaction after verify fail:", cancelErr);
                                    }
                                }
                                setSubmitting(false);
                            }
                        } catch (err) {
                            console.error("Payment verification API exception:", err);
                            message.warning("Payment verification is pending due to network issues. Please check your Order History in a few minutes.");
                            setSubmitting(false);
                            navigate("/buyer/orders");
                        }
                    },
                    prefill: {
                        name: userProfile?.firstName + " " + userProfile?.lastName,
                        email: userProfile?.email,
                        contact: userProfile?.phone,
                    },
                    theme: {
                        color: "#6c5ce7",
                    },
                    modal: {
                        ondismiss: async function () {
                            if (isPaymentSuccessful) {
                                return;
                            }
                            console.log("Razorpay checkout modal dismissed by user");
                            message.warning("Payment cancelled. Your order was not placed.");
                            setSubmitting(true);
                            if (transactionId) {
                                try {
                                    await OrdersServ.cancelPending({ transactionId });
                                } catch (err) {
                                    console.error("Error calling cancel pending on modal dismiss:", err);
                                }
                            }
                            setSubmitting(false);
                        }
                    }
                };

                const rzp1 = new window.Razorpay(options);
                rzp1.on("payment.failed", function (response) {
                    message.error("Payment Failed: " + response.error.description);
                });
                rzp1.open();
                setSubmitting(false);
            } else {
                finalizeOrder(createdOrders);
            }
        } catch (err) {
            console.error("Order Error:", err);
            message.error(err.message || "Failed to place order");
            setSubmitting(false);
        }
    };

    const finalizeOrder = (createdOrdersList = []) => {
        NotificationService.success(
            "Order Placed Successfully",
            "Your order has been placed and is being processed."
        );
        window.dispatchEvent(new Event("credits-refresh"));
        if (!items) refreshCart();

        const displayOrderId = createdOrdersList.length > 0 ? createdOrdersList[0].orderId : "N/A";

        // Redirect to order success page
        navigate("/order-success", { state: { orderId: displayOrderId, totalAmount: finalAmount } });
    };





    const renderStepContent = () => {
        const globalSlot = selectedSlot || '09:00 - 12:00';
        const isNormal = selectedSlot === 'Normal';


        // Calculate max ETA for Normal Delivery
        const getMaxETA = () => {
            if (!estimates || Object.keys(estimates).length === 0) return "Delivery in 30-45 mins";

            let explicitTime = null;
            shopIds.forEach(id => {
                const est = estimates[id.toString().toLowerCase()];
                if (est && est.deliveryTime) explicitTime = `Delivery in ${est.deliveryTime}`;
            });
            if (explicitTime) return explicitTime;

            let latest = dayjs();
            shopIds.forEach(id => {
                const est = estimates[id.toString().toLowerCase()];
                if (est && est.eta) {
                    const eta = dayjs(est.eta);
                    if (eta.isAfter(latest)) latest = eta;
                } else if (est && est.duration && est.duration.value) {
                    const eta = dayjs().add(est.duration.value + 1800, 'second');
                    if (eta.isAfter(latest)) latest = eta;
                }
            });
            if (latest.isSame(dayjs(), 'minute')) latest = dayjs().add(45, 'minute');

            const diffMinutes = Math.max(1, latest.diff(dayjs(), 'minute'));

            if (diffMinutes >= 1440) {
                const days = Math.round(diffMinutes / 1440);
                return `Delivery in ${days} day${days > 1 ? 's' : ''}`;
            } else if (diffMinutes >= 60) {
                const hours = Math.round(diffMinutes / 60);
                return `Delivery in ${hours} hour${hours > 1 ? 's' : ''}`;
            }
            return `Delivery in ${diffMinutes} mins`;
        };

        return (
            <div className="checkout-group-container">
                {/* 1. Review Items Section (Order Summary) */}
                <section className="checkout-section">
                    <div className="checkout-section-header">
                        <div className="pcu-flex pcu-items-center pcu-gap-8">
                            <IconPackage size={20} style={{ marginTop: "-10px" }} color="var(--nearzon-purple)" />
                            <span className="text-subheader pcu-margin-0">Order Summary</span>
                        </div>
                        <span className="text-sub-description">{checkoutItems.length} {checkoutItems.length === 1 ? 'Item' : 'Items'}</span>
                    </div>

                    <div className="checkout-card-wrapper pcu-radius-12">
                        {shopIds.map((sId, shopIdx) => {
                            const sIdStr = sId.toString();
                            const shopItems = checkoutItems.filter(item => {
                                const p = item.product || item;
                                const pShopId = p.shopId?._id || p.shopId;
                                return pShopId === sIdStr;
                            });

                            if (shopItems.length === 0) return null;

                            return (
                                <div key={sIdStr} className={shopIdx !== shopIds.length - 1 ? 'pcu-border-bottom-muted' : ''}>
                                    <div className="checkout-shop-boundary pcu-padding-x-12 pcu-padding-y-8">
                                        <span className="text-sub-description pcu-uppercase pcu-letter-spacing-1 pcu-font-600">{shopsData[sIdStr]?.name || 'Shop'}</span>
                                        {shopItems.map((item, idx) => {
                                            const p = item.product || item;
                                            const { price } = resolvePriceAndMrp(item);

                                            return (
                                                <div key={idx} className="checkout-item-row pcu-padding-y-10">
                                                    <div className="checkout-item-image-box" style={{ width: '48px', height: '48px' }}>
                                                        <SafeImage
                                                            src={p.imageUrl || (p.images && p.images[0]) || p.image}
                                                            alt={p.name}
                                                            className="pcu-image-cover"
                                                        />
                                                    </div>
                                                    <div className="checkout-item-info">
                                                        <div className="checkout-item-title-row pcu-items-center">
                                                            {/* Left: name + variants + stepper */}
                                                            <div className="pcu-flex-1 pcu-min-w-0">
                                                                <span className="text-subtitle pcu-display-block pcu-ellipsis pcu-font-600">
                                                                    {p.name}
                                                                </span>
                                                                {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                                                                    <span className="text-sub-description pcu-display-block pcu-ellipsis">
                                                                        {Object.entries(item.selectedOptions).map(([k, v]) => `${k}: ${formatUnit(v, k)}`).join(', ')}
                                                                    </span>
                                                                )}
                                                                <div className="checkout-qty-stepper">
                                                                    <button
                                                                        className="checkout-qty-btn"
                                                                        onClick={() => updateQuantity(item._id || item.id, Math.max(1, (item.quantity || 1) - 1))}
                                                                    >
                                                                        <IconMinus size={11} />
                                                                    </button>
                                                                    <span className="checkout-qty-value">{item.quantity || 1}</span>
                                                                    <button
                                                                        className="checkout-qty-btn"
                                                                        onClick={() => updateQuantity(item._id || item.id, (item.quantity || 1) + 1)}
                                                                    >
                                                                        <IconPlus size={11} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            {/* Right: price only */}
                                                            <span className="text-subtitle pcu-font-600 pcu-padding-left-8">₹{Math.round(price * (item.quantity || 1))}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}

                        <div
                            className="pcu-padding-12 pcu-border-top-muted pcu-flex pcu-justify-between pcu-items-center"
                            style={{
                                background: 'var(--color-bg-surface)',
                                paddingLeft: '20px',
                                paddingRight: '20px',
                                marginBottom: "10px",
                                marginTop: "40px"
                            }}
                        >
                            <div className="pcu-flex pcu-items-center pcu-gap-8" style={{ marginTop: "10px" }}>
                                <span className="text-sub-description pcu-font-600">Total Quantity:</span>
                                <Tag color="purple" className="pcu-font-700 pcu-margin-0">{totals.totalQty}</Tag>
                            </div>
                            <div className="pcu-flex pcu-items-center pcu-gap-8" style={{ marginTop: "10px" }}>
                                <span className="text-sub-description pcu-font-600">Total Price:</span>
                                <span className="pcu-font-700 pcu-color-black" style={{ fontSize: '14px' }}>₹{Math.round(subtotal)}</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 2. Delivery Address Section */}
                <section className="checkout-section">
                    <div className="checkout-section-header">
                        <div className="pcu-flex pcu-items-center pcu-gap-8">
                            <IconMapPin size={20} style={{ marginTop: "-10" }} color="var(--nearzon-orange)" />
                            <span className="text-subheader pcu-margin-0">Delivery Address</span>
                        </div>
                        <Button
                            type="link"
                            onClick={() => setShowAddressGrid(true)}
                            className="pcu-color-orange pcu-padding-0 pcu-font-600"
                        >
                            {selectedAddressId ? 'Change Address' : 'Select Address'}
                        </Button>
                    </div>

                    {(!showAddressGrid && selectedAddressId) ? (
                        <div className="checkout-address-card">
                            <div className="checkout-address-details">
                                <div className="checkout-address-header-row">
                                    <span className="text-title">Deliver To</span>
                                    <Tag color="cyan" className="pcu-radius-4 pcu-capitalize pcu-font-10">
                                        {selectedAddress?.addressType || 'Home'}
                                    </Tag>
                                </div>
                                <span className="text-description checkout-address-text pcu-display-block">
                                    {[selectedAddress?.address, selectedAddress?.street, selectedAddress?.city, selectedAddress?.state].filter(Boolean).join(', ')}
                                    {selectedAddress?.zipCode ? ` - ${selectedAddress.zipCode}` : ''}
                                </span>
                                <div className="pcu-margin-top-8 pcu-flex pcu-items-center pcu-gap-4">
                                    <span className="text-sub-description">Phone:</span>
                                    <span className="text-subtitle pcu-font-600">{selectedAddress?.phoneNumber || userProfile?.phone || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    ) : showAddressGrid ? (
                        <div className="checkout-address-grid-container pcu-radius-12">
                            <Row gutter={[12, 12]}>
                                {addressList.map(addr => (
                                    <Col xs={24} sm={12} key={addr._id || addr.id}>
                                        <Card
                                            size="small"
                                            hoverable
                                            className={`pcu-radius-10 ${selectedAddressId === (addr._id || addr.id) ? 'checkout-selected-border' : ''}`}
                                            onClick={() => {
                                                setSelectedAddressId(addr._id || addr.id);
                                                setShowAddressGrid(false);
                                            }}
                                        >
                                            <div className="pcu-flex-between pcu-align-start pcu-margin-bottom-2">
                                                <span className="text-subtitle pcu-font-600">{addr.name || addr.addressType}</span>
                                                <Tag color="blue" className="pcu-font-9 pcu-margin-0">{addr.addressType}</Tag>
                                            </div>
                                            <span className="text-sub-description pcu-display-block pcu-ellipsis">
                                                {addr.address || addr.street}
                                            </span>
                                        </Card>
                                    </Col>
                                ))}
                                <Col xs={24}>
                                    <Button
                                        block
                                        size="small"
                                        icon={<IconPlus size={14} />}
                                        onClick={() => {
                                            setIsEditingAddress(false);
                                            setEditingAddressDetails(null);
                                            setAddressAddModalOpen(true);
                                        }}
                                        className="pcu-border-dashed pcu-radius-8"
                                    >
                                        Add New Address
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    ) : (
                        <div className="checkout-address-card" style={{ border: '1px dashed #d9d9d9', backgroundColor: '#fafafa' }}>
                            <div className="checkout-address-details">
                                <div className="checkout-address-header-row">
                                    <span className="text-title">Deliver To</span>
                                    <Tag color="orange" className="pcu-radius-4 pcu-capitalize pcu-font-10">
                                        Current Location
                                    </Tag>
                                </div>
                                <span className="text-description checkout-address-text pcu-display-block">
                                    {addressName || 'Unknown Location'}
                                </span>
                                <div className="pcu-margin-top-8 pcu-flex pcu-items-center pcu-gap-4">
                                    <span className="text-sub-description" style={{ color: '#d9534f' }}>Please add full address details to proceed.</span>
                                </div>
                                <Button
                                    type="primary"
                                    size="middle"
                                    className="pcu-margin-top-8 pcu-bg-purple pcu-radius-10"
                                    onClick={() => setAddressAddModalOpen(true)}
                                >
                                    Add Full Address
                                </Button>
                            </div>
                        </div>
                    )}
                </section>

                {/* 3. Delivery Schedule Section */}
                <section className="checkout-section">
                    <div className="checkout-section-header">
                        <div className="pcu-flex pcu-items-center pcu-gap-8">
                            <IconClock size={20} style={{ marginTop: "-10" }} color="var(--nearzon-purple)" />
                            <span className="text-subheader pcu-margin-0">Delivery Options</span>
                        </div>
                    </div>

                    <div className="checkout-flex-column-gap-12">
                        {/* Option 1: Normal Delivery */}
                        <div
                            onClick={() => updateGlobalSlot('Normal')}
                            className={`checkout-delivery-option ${isNormal ? 'checkout-delivery-option-active' : 'checkout-delivery-option-inactive'} pcu-padding-12`}
                        >
                            <div className="checkout-delivery-header">
                                <div className="pcu-flex pcu-items-center pcu-gap-12">
                                    <div className={`checkout-delivery-icon-box ${isNormal ? 'checkout-delivery-icon-active' : 'checkout-delivery-icon-inactive'} pcu-padding-8`}>
                                        <IconTruck size={20} />
                                    </div>
                                    <div>
                                        <span className="text-title pcu-display-block">Express Delivery</span>
                                        <div className="checkout-eta-badge pcu-padding-x-8 pcu-padding-y-2 pcu-margin-0">
                                            <IconTruck size={12} />
                                            {getMaxETA()}
                                        </div>
                                    </div>
                                </div>
                                {isNormal && <IconCheck size={20} color="var(--nearzon-orange)" />}
                            </div>
                        </div>

                        {/* Option 2: Schedule for Later */}
                        <div className={`checkout-delivery-option ${!isNormal ? 'checkout-delivery-option-active' : 'checkout-delivery-option-inactive'} pcu-padding-12`}>
                            <div
                                onClick={() => isNormal && updateGlobalSlot('09:00 - 12:00')}
                                className="pcu-flex-between pcu-cursor-pointer"
                            >
                                <div className="pcu-flex pcu-items-center pcu-gap-12">
                                    <div className={`checkout-delivery-icon-box ${!isNormal ? 'checkout-delivery-icon-active' : 'checkout-delivery-icon-inactive'} pcu-padding-8`}>
                                        <IconCalendar size={20} />
                                    </div>
                                    <div>
                                        <span className="text-title pcu-display-block">Scheduled Delivery</span>
                                        <span className="text-sub-description">Choose your preferred delivery time</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Persistent Scheduling Details - Always Visible */}
                        <div className="pcu-margin-top-12 pcu-padding-top-12 pcu-border-top">
                            <Row gutter={12}>
                                <Col span={24} className="pcu-margin-bottom-12">
                                    <div className="checkout-date-scroll">
                                        {[...Array(7)].map((_, i) => {
                                            const date = dayjs().add(i, 'day');
                                            const sId = shopIds[0]?.toString();
                                            const isActive = scheduledDate && dayjs(scheduledDate).isSame(date, 'day');

                                            return (
                                                <div
                                                    key={i}
                                                    className={`checkout-date-chip ${isActive ? 'active' : ''}`}
                                                    onClick={() => updateGlobalDate(date)}
                                                >
                                                    <Text className="date-day">{date.format('ddd')}</Text>
                                                    <Text strong className="date-num">{date.format('DD')}</Text>
                                                    <Text className="date-month">{date.format('MMM')}</Text>
                                                </div>
                                            );
                                        })}

                                    </div>
                                </Col>
                                <Col span={24}>
                                    <div className="time-slot-grid">
                                        {[
                                            { val: '09:00 - 12:00', label: 'Morning', display: '9-12 PM', icon: <IconSun size={16} />, endHour: 12 },
                                            { val: '13:00 - 16:00', label: 'Afternoon', display: '1-4 PM', icon: <IconCloud size={16} />, endHour: 16 },
                                            { val: '18:00 - 21:00', label: 'Evening', display: '6-9 PM', icon: <IconMoon size={16} />, endHour: 21 },
                                            { val: 'custom', label: 'Custom', display: 'Pick Time', icon: <IconClock size={16} />, endHour: 24 }
                                        ].map(slot => {
                                            const globalDateValue = scheduledDate;
                                            const now = dayjs();
                                            const isToday = globalDateValue && dayjs(globalDateValue).isSame(now, 'day');
                                            const isPast = isToday && now.hour() >= slot.endHour;


                                            return (
                                                <div
                                                    key={slot.val}
                                                    className={`time-slot-card ${globalSlot === slot.val ? 'active' : ''} ${isPast ? 'disabled' : ''} pcu-padding-x-8 pcu-padding-y-6`}
                                                    onClick={() => !isPast && updateGlobalSlot(slot.val)}
                                                >
                                                    <div className={`pcu-flex-center ${globalSlot === slot.val ? 'pcu-color-orange' : 'pcu-color-muted'}`}>
                                                        {slot.icon}
                                                    </div>
                                                    <div className="time-slot-content">
                                                        <span className="text-sub-description pcu-font-600 pcu-color-title pcu-display-block">{slot.label}</span>
                                                        <span className="text-sub-description">{slot.display}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Col>
                            </Row>

                            {globalSlot === 'custom' && (
                                <div className="checkout-custom-picker-container pcu-margin-top-12">
                                    <div className="checkout-custom-picker-card-combined">
                                        <div className="checkout-custom-picker-icon">
                                            <IconCalendar size={18} />
                                        </div>
                                        <div className="checkout-custom-picker-field">
                                            <span className="checkout-custom-picker-label">Custom Date & Time Slot (+₹{CUSTOM_CHARGE})</span>
                                            <DatePicker
                                                showTime={{ format: 'hh:mm A', use12Hours: true, minuteStep: 15 }}
                                                className="checkout-custom-picker-input-combined"
                                                format="DD MMM YYYY, hh:mm A"
                                                disabledDate={(current) => current && current < dayjs().startOf('day')}
                                                disabledTime={(current) => {
                                                    if (current && current.isSame(dayjs(), 'day')) {
                                                        return {
                                                            disabledHours: () => {
                                                                const hours = [];
                                                                for (let i = 0; i < dayjs().hour(); i++) hours.push(i);
                                                                return hours;
                                                            },
                                                            disabledMinutes: (selectedHour) => {
                                                                const minutes = [];
                                                                if (selectedHour === dayjs().hour()) {
                                                                    for (let i = 0; i < dayjs().minute(); i++) minutes.push(i);
                                                                }
                                                                return minutes;
                                                            }
                                                        };
                                                    }
                                                    return {};
                                                }}
                                                value={customTime || scheduledDate}
                                                onChange={(value) => {
                                                    if (value) {
                                                        setScheduledDate(value);
                                                        setCustomTime(value);
                                                    }
                                                }}
                                                placeholder="Select Date and Time"
                                                allowClear={false}
                                                bordered={false}
                                                popupClassName="premium-datetime-popup"
                                                suffixIcon={null}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* 4. Payment Method Section */}
                <section className="checkout-section" style={Math.round(finalAmount) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                    <div className="checkout-section-header pcu-flex pcu-flex-between pcu-items-center">
                        <div className="pcu-flex pcu-items-center pcu-gap-8">
                            <IconCreditCard size={20} color="var(--nearzon-orange)" />
                            <span className="text-subheader pcu-margin-0">Payment Method</span>
                        </div>
                        {Math.round(finalAmount) === 0 && (
                            <Tag color="green" className="pcu-font-600 pcu-margin-0">No Payment Required</Tag>
                        )}
                    </div>

                    <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12}>
                            <div
                                onClick={() => setPaymentMethod('ONLINE')}
                                className={`checkout-payment-method-card ${paymentMethod === 'ONLINE' ? 'active' : ''}`}
                            >
                                <div className={`checkout-payment-icon-wrapper ${paymentMethod === 'ONLINE' ? 'pcu-color-orange' : 'pcu-color-muted'}`}>
                                    <IconCreditCard size={18} />
                                </div>
                                <div className="checkout-payment-content">
                                    <span className="text-sub-description pcu-font-600 pcu-color-title pcu-display-block">Online Payment</span>
                                    <span className="text-sub-description">Razorpay (Cards, UPI, Netbanking)</span>
                                </div>
                            </div>
                        </Col>
                        <Col xs={24} sm={12}>
                            <div
                                onClick={() => setPaymentMethod('COD')}
                                className={`checkout-payment-method-card ${paymentMethod === 'COD' ? 'active' : ''}`}
                            >
                                <div className={`checkout-payment-icon-wrapper ${paymentMethod === 'COD' ? 'pcu-color-purple' : 'pcu-color-muted'}`}>
                                    <IconTruck size={18} />
                                </div>
                                <div className="checkout-payment-content">
                                    <span className="text-sub-description pcu-font-600 pcu-color-title pcu-display-block">Cash on Delivery</span>
                                    <span className="text-sub-description">Pay when you receive</span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </section>

            </div>
        );
    };

    const content = (
        <div className={`checkout-page-container ${open !== undefined ? 'checkout-in-modal' : ''}`}>
            <div className="checkout-page-body">
                <div
                    ref={topRef}
                    className="checkout-modal-scroll-area"
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24} lg={16}>
                            <div className="checkout-step-content">
                                {renderStepContent()}
                            </div>

                        </Col>

                        <Col xs={24} lg={8}>
                            <Card
                                title={<span className="text-subtitle pcu-font-600 pcu-uppercase pcu-letter-spacing-1">Price Details</span>}
                                className="checkout-sidebar-card"
                                styles={{ header: { borderBottom: '1px solid var(--color-border-primary)' } }}
                            >
                                <div className="pcu-margin-bottom-20">
                                    <div className="pcu-flex pcu-flex-between pcu-align-baseline pcu-margin-bottom-12">
                                        <Text className="pcu-color-body">Total MRP ({checkoutItems.length} {checkoutItems.length === 1 ? 'Item' : 'Items'})</Text>
                                        <Text className="pcu-color-title pcu-strikethrough pcu-opacity-60">₹{totalMrp}</Text>
                                    </div>
                                    <div className="pcu-flex pcu-flex-between pcu-align-baseline pcu-margin-bottom-12">
                                        <Text className="pcu-color-body">Price After Shop Discount</Text>
                                        <Text strong className="pcu-color-title">₹{subtotal}</Text>
                                    </div>
                                    {itemSavings > 0 && (
                                        <div className="pcu-flex pcu-flex-between pcu-align-baseline pcu-margin-bottom-12">
                                            <Text className="pcu-color-success">🎁 Item Discount</Text>
                                            <Text strong className="pcu-color-success">- ₹{Number(itemSavings).toFixed(2)}
                                            </Text>
                                        </div>
                                    )}
                                    <div style={{ borderTop: '1px dashed #eee', paddingTop: 8, marginBottom: 12 }} className="pcu-flex pcu-flex-between pcu-align-baseline">
                                        <Text strong className="pcu-color-body">Subtotal</Text>
                                        <Text strong className="pcu-color-title">₹{subtotal}</Text>
                                    </div>
                                    <div className="pcu-flex pcu-flex-between pcu-align-baseline pcu-margin-bottom-8">
                                        <Text className="pcu-color-body">🚚 Delivery Fee</Text>
                                        {estLoading ? (
                                            <Text style={{ color: '#9ca3af', fontSize: 12 }}>Calculating…</Text>
                                        ) : (
                                            <Text strong className={deliveryFee === 0 ? 'pcu-color-success' : 'pcu-color-orange'}>
                                                {deliveryFee === 0 ? 'FREE' : `+ ₹${deliveryFee}`}
                                            </Text>
                                        )}
                                    </div>
                                    {deliveryFeeUpdated && (
                                        <div style={{
                                            background: '#f0fdf4',
                                            border: '1px solid #bbf7d0',
                                            borderRadius: 8,
                                            padding: '6px 10px',
                                            marginBottom: 8,
                                            fontSize: 11,
                                            color: '#15803d',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6
                                        }}>
                                            <span>✅</span>
                                            <span>Delivery fee updated based on your delivery address.</span>
                                        </div>
                                    )}
                                    <div className="pcu-flex pcu-flex-between pcu-align-baseline pcu-margin-bottom-8">
                                        <Text className="pcu-color-body">📦 Handling Fee</Text>
                                        {(billDetails?.handlingFee || 0) > 0 ? (
                                            <Text strong className="pcu-color-orange">+ ₹{billDetails.handlingFee}</Text>
                                        ) : (
                                            <Text strong className="pcu-color-success">FREE</Text>
                                        )}
                                    </div>
                                </div>

                                {/* Order Total before discounts */}
                                <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '10px 14px', marginBottom: 16, borderLeft: '3px solid #d9d9d9' }}>
                                    <div className="pcu-flex pcu-flex-between">
                                        <span className="text-title pcu-font-600">Order Total</span>
                                        <span className="text-title pcu-font-600">₹{Math.round(totalAmount)}</span>
                                    </div>
                                    <Text type="secondary" style={{ fontSize: 11 }}>Subtotal + Delivery + Handling</Text>
                                </div>

                                {/* Coupon / Offer Section */}
                                <div className="pcu-margin-bottom-16">
                                    {!appliedCoupon ? (
                                        <>
                                            <div className="premium-coupon-input-wrapper">
                                                <Input
                                                    variant="borderless"
                                                    placeholder="Enter Coupon Code"
                                                    value={couponCode}
                                                    className="premium-coupon-input"
                                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                                    onPressEnter={() => handleApplyCoupon()}
                                                    prefix={<IconTag size={18} className="pcu-opacity-50" />}
                                                />
                                                <Button
                                                    type="primary"
                                                    loading={applyingCoupon}
                                                    onClick={() => handleApplyCoupon()}
                                                    className="premium-coupon-apply-btn"
                                                >
                                                    Apply
                                                </Button>
                                            </div>
                                            {couponError && (
                                                <div className="pcu-color-danger pcu-font-12 pcu-margin-top-8 pcu-padding-x-4">
                                                    {couponError}
                                                </div>
                                            )}

                                            {(availableCoupons.length > 0 || availableOffers.length > 0) && (
                                                <div className="pcu-margin-top-16">
                                                    <Text type="secondary" className="pcu-font-11 pcu-display-block pcu-margin-bottom-8 pcu-letter-spacing-1">🏷️ AVAILABLE OFFERS</Text>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                        {availableOffers.filter(o => o.isActive !== false).map(o => {
                                                            const expiryDate = o.expiryDate ? new Date(o.expiryDate) : (o.endDate ? new Date(o.endDate) : null);
                                                            const isExpired = expiryDate ? expiryDate < new Date() : false;
                                                            if (isExpired) return null;
                                                            const dVal = parseFloat(o.discountValue ?? o.discount ?? 0) || 0;
                                                            const dType = o.discountType ?? (o.isPercentage ? 'percentage' : 'amount');
                                                            const savings = dType === 'percentage' ? Math.round((subtotal * dVal) / 100) : Math.min(dVal, subtotal);
                                                            return (
                                                                <div key={o._id || o.code} style={{ border: '1.5px dashed #d9b3ff', borderRadius: 10, padding: '10px 14px', background: '#fdf6ff' }}>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                                                                        <div>
                                                                            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--nearzon-purple)', letterSpacing: 1 }}>{o.code}</span>
                                                                            <span style={{ display: 'block', fontSize: 11, color: '#888', marginTop: 2 }}>{o.title}</span>
                                                                        </div>
                                                                        <Tag color={dType === 'percentage' ? 'purple' : 'orange'} style={{ marginLeft: 8, flexShrink: 0 }}>
                                                                            {dType === 'percentage' ? `${dVal}% OFF` : `₹${dVal} OFF`}
                                                                        </Tag>
                                                                    </div>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <span style={{ fontSize: 11, color: '#52c41a', fontWeight: 600 }}>
                                                                            {subtotal > 0 ? `Save ₹${savings} on this order` : 'Add items to see savings'}
                                                                        </span>
                                                                        <Button size="small" type="primary"
                                                                            style={{ borderRadius: 16, fontSize: 11, fontWeight: 600, height: 26, padding: '0 12px' }}
                                                                            onClick={() => handleApplyCoupon(o.code)}
                                                                        >Apply</Button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                        {availableCoupons.map(c => {
                                                            const savings = c.discountType === 'percentage' ? Math.round((subtotal * c.discountValue) / 100) : Math.min(c.discountValue, subtotal);
                                                            return (
                                                                <div key={c._id || c.code} style={{ border: '1.5px dashed #b7c5ff', borderRadius: 10, padding: '10px 14px', background: '#f0f5ff' }}>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                                                                        <div>
                                                                            <span style={{ fontWeight: 700, fontSize: 13, color: '#1d39c4', letterSpacing: 1 }}>{c.code}</span>
                                                                            <span style={{ display: 'block', fontSize: 11, color: '#888', marginTop: 2 }}>Min order: ₹{c.minOrderAmount}</span>
                                                                        </div>
                                                                        <Tag color={c.discountType === 'percentage' ? 'blue' : 'green'} style={{ marginLeft: 8, flexShrink: 0 }}>
                                                                            {c.discountType === 'percentage' ? `${c.discountValue}% OFF` : `₹${c.discountValue} OFF`}
                                                                        </Tag>
                                                                    </div>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <span style={{ fontSize: 11, color: '#52c41a', fontWeight: 600 }}>
                                                                            {subtotal >= (c.minOrderAmount || 0) ? `Save ₹${savings} on this order` : `Add ₹${(c.minOrderAmount || 0) - subtotal} more to unlock`}
                                                                        </span>
                                                                        <Button size="small" type="primary"
                                                                            style={{ borderRadius: 16, fontSize: 11, fontWeight: 600, height: 26, padding: '0 12px' }}
                                                                            onClick={() => handleApplyCoupon(c.code)}
                                                                            disabled={subtotal < (c.minOrderAmount || 0)}
                                                                        >Apply</Button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="premium-applied-coupon-box">
                                            <div className="pcu-flex pcu-items-center pcu-gap-12">
                                                <div className="pcu-flex-column">
                                                    <div className="pcu-flex pcu-items-center pcu-gap-8">
                                                        <span className="premium-coupon-tag-p">{appliedCoupon}</span>
                                                        <span className="pcu-font-12 pcu-color-success pcu-font-700">APPLIED ✅</span>
                                                    </div>
                                                    <div className="pcu-font-11 pcu-color-success pcu-margin-top-4 pcu-font-500">
                                                        🎉 You saved <strong>₹{Math.round(couponDiscount + (appliedCouponDetails?.deliveryDiscount || 0))}</strong> on this order!
                                                    </div>
                                                </div>
                                            </div>
                                            <Button
                                                type="link"
                                                danger
                                                size="small"
                                                onClick={handleRemoveCoupon}
                                                className="pcu-font-600 pcu-padding-0"
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    )}
                                </div>


                                {/* Step-by-step deductions after Order Total */}
                                {couponDiscount > 0 && (
                                    <div className="pcu-flex pcu-flex-between pcu-margin-bottom-10">
                                        <div className="pcu-flex-center pcu-gap-4">
                                            <Text className="pcu-color-success">🏷️ Offer / Coupon Discount</Text>
                                            {appliedCouponDetails && (
                                                <Text type="secondary" className="pcu-font-11">
                                                    ({appliedCouponDetails.discountType === 'percentage' ? `${appliedCouponDetails.discountValue}%` : `₹${appliedCouponDetails.discountValue}`})
                                                </Text>
                                            )}
                                        </div>
                                        <Text strong className="pcu-color-success">- ₹{Math.round(couponDiscount)}</Text>
                                    </div>
                                )}

                                {useCredits && (
                                    <div className="pcu-flex pcu-flex-between pcu-margin-bottom-16">
                                        <Text className="pcu-color-orange">Credits Applied</Text>
                                        <Text strong className="pcu-color-orange">- ₹{creditsToUse}</Text>
                                    </div>
                                )}

                                {userProfile?.credits > 0 && (
                                    <div className="checkout-credits-box">
                                        <div className="pcu-flex pcu-flex-between pcu-align-center">
                                            <div>
                                                <Text strong className="pcu-font-13">Available Credits: ₹{userProfile.credits}</Text>
                                                <br />
                                                <Text type="secondary" className="pcu-font-11">Apply credits to save more</Text>
                                            </div>
                                            <Radio.Group
                                                size="small"
                                                onChange={(e) => handleUseCreditsToggle(e.target.value)}
                                                value={useCredits}
                                            >
                                                <Radio value={true}>Use Credits</Radio>
                                                <Radio value={false}>Don't Use Credits</Radio>
                                            </Radio.Group>
                                        </div>
                                    </div>
                                )}

                                <Divider className="checkout-final-divider" />

                                <div className="pcu-flex pcu-flex-between pcu-items-center">
                                    <span className="text-subheader pcu-margin-0">Total Payable</span>
                                    <span className="text-header pcu-color-orange pcu-margin-0 pcu-font-800" style={{ fontSize: '28px' }}>₹{Math.round(finalAmount)}</span>
                                </div>

                                {totalSavings > 0 && (
                                    <div className="checkout-bottom-savings">
                                        <span className="pcu-color-success pcu-font-600">🎉 You saved ₹{Math.round(totalSavings)} on this order</span>
                                    </div>
                                )}

                                <div className="pcu-margin-top-12 pcu-text-center">
                                    <span className="text-sub-description pcu-display-block">
                                        {useCredits ? (
                                            <span className="pcu-color-success pcu-font-600">Credits applied: ₹{creditsToUse}</span>
                                        ) : (
                                            `You can save up to  ₹${userProfile?.credits || 0} with credits`
                                        )}
                                    </span>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Bottom sticky action bar (Universal) */}
            <div className={`checkout-actions-bar ${open !== undefined ? 'checkout-modal-actions' : ''}`}>
                <div className="checkout-sticky-footer-container" style={{ flexDirection: 'column', alignItems: 'center' }}>
                    {cart?.shopAvailability && !cart.shopAvailability.isOpen ? (
                        <div style={{ width: '100%', maxWidth: '400px', textAlign: 'center', marginBottom: '8px' }}>
                            <div style={{ background: '#fee2e2', color: '#dc2626', padding: '8px', borderRadius: '8px', fontWeight: 600, fontSize: '13px' }}>
                                🚨 Shop Closed. {cart.shopAvailability.reopenMessage}
                            </div>
                            <Button
                                type="primary"
                                size="large"
                                disabled
                                className="checkout-btn-confirm pcu-margin-top-8"
                                style={{ flex: 1, margin: 0, width: '100%', maxWidth: '400px', opacity: 0.5 }}
                            >
                                Checkout Unavailable
                            </Button>
                        </div>
                    ) : (
                        <Button
                            type="primary"
                            size="large"
                            loading={submitting}
                            onClick={() => {
                                handlePlaceOrder();
                            }}
                            className="checkout-btn-confirm"
                            style={{ flex: 1, margin: 0, width: '100%', maxWidth: '400px' }}
                        >
                            {Math.round(finalAmount) === 0 || paymentMethod === 'COD' ? "Place Order 🎉" : "Pay & Confirm Order"}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            {open !== undefined ? (
                <Modal
                    open={open}
                    onCancel={onClose}
                    footer={null}
                    width={modalWidth}
                    centered
                    className="checkout-main-wrapper-modal"
                    styles={{ body: { padding: 0 } }}
                >
                    {content}
                </Modal>
            ) : (
                content
            )}


            <DeliveryAddressModal
                open={addressAddModalOpen}
                onClose={() => setAddressAddModalOpen(false)}
                addressDetails={editingAddressDetails}
                isEditing={isEditingAddress}
                refreshAddress={(ref) => {
                    setAddressAddModalOpen(false);
                    setIsEditingAddress(false);
                    setEditingAddressDetails(null);
                    if (ref) loadAddress();
                }}
            />

            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingBottom: '12px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            background: '#5a31f4',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff'
                        }}>
                            {/* Wallet Icon */}
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="4" width="20" height="16" rx="2" />
                                <path d="M12 4v16" />
                            </svg>
                        </div>
                        <span style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>Apply Credits</span>
                    </div>
                }
                open={confirmCreditModal}
                onCancel={() => setConfirmCreditModal(false)}
                footer={null}
                width={480}
                centered
                closable={true}
                closeIcon={
                    <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        background: '#f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6b7280',
                        marginTop: '4px'
                    }}>
                        <span style={{ fontSize: '14px', fontWeight: 'bold', lineHeight: 1 }}>✕</span>
                    </div>
                }
                styles={{
                    content: {
                        borderRadius: "20px",
                        padding: "24px",
                        boxShadow: "0 20px 25px -5px rgba(0,0,0,0.05), 0 10px 10px -5px rgba(0,0,0,0.05)"
                    }
                }}
            >
                <div style={{ marginTop: '20px' }}>
                    {/* Purple Inner Card Block */}
                    <div style={{
                        background: "#faf9ff",
                        border: "1px solid #edeafc",
                        borderRadius: "12px",
                        padding: "28px 24px",
                        textAlign: "center",
                        position: "relative",
                        marginBottom: "24px"
                    }}>
                        {/* Sparkles Decoration */}
                        {/* Top-Left Sparkle */}
                        <div style={{ position: 'absolute', top: '24px', left: '30%', color: '#a78bfa', fontSize: '12px' }}>✦</div>
                        {/* Top-Right Sparkle */}
                        <div style={{ position: 'absolute', top: '28px', right: '30%', color: '#818cf8', fontSize: '10px' }}>✦</div>
                        {/* Bottom-Left Sparkle */}
                        <div style={{ position: 'absolute', bottom: '28px', left: '25%', color: '#cbd5e1', fontSize: '14px' }}>○</div>
                        {/* Bottom-Right Sparkle */}
                        <div style={{ position: 'absolute', bottom: '24px', right: '25%', color: '#34d399', fontSize: '12px' }}>✦</div>

                        {/* Floating circle with ticket */}
                        <div style={{
                            width: "64px",
                            height: "64px",
                            borderRadius: "50%",
                            background: "#fff",
                            border: "1px solid #edeafc",
                            boxShadow: "0 4px 12px rgba(90, 49, 244, 0.06)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 18px"
                        }}>
                            <div style={{ position: 'relative', width: '32px', height: '20px', border: '2.5px solid #5a31f4', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ fontSize: '11px', fontWeight: '900', color: '#5a31f4', marginTop: '-1px' }}>₹</span>
                                <div style={{ position: 'absolute', top: '50%', left: '-3px', transform: 'translateY(-50%)', width: '4px', height: '6px', background: '#fff', border: '1.5px solid #5a31f4', borderLeft: 'none', borderTopRightRadius: '3px', borderBottomRightRadius: '3px' }}></div>
                                <div style={{ position: 'absolute', top: '50%', right: '-3px', transform: 'translateY(-50%)', width: '4px', height: '6px', background: '#fff', border: '1.5px solid #5a31f4', borderRight: 'none', borderTopLeftRadius: '3px', borderBottomLeftRadius: '3px' }}></div>
                            </div>
                        </div>

                        {/* Main Description */}
                        <p style={{
                            fontSize: "15.5px",
                            color: "#1f2937",
                            fontWeight: "500",
                            lineHeight: "1.5",
                            margin: "0 0 20px"
                        }}>
                            Use <span style={{ color: "#5a31f4", fontWeight: "800", fontSize: '17px' }}>₹{Math.min(userProfile?.credits || 0, totalAmount)}</span> from your Nearzon Credits<br />for this order
                        </p>

                        {/* Available Credits Card */}
                        <div style={{
                            background: "#ffffff",
                            border: "1px solid #f1f0f9",
                            borderRadius: "8px",
                            padding: "12px 16px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5a31f4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
                                    <path d="M4 6v12c0 1.1.9 2 2 2h14v-4" />
                                    <path d="M18 12a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h4v-6Z" />
                                </svg>
                                <span style={{ fontSize: "13px", color: "#4b5563", fontWeight: "600" }}>Available Credits</span>
                            </div>
                            <span style={{ fontSize: "14px", color: "#10b981", fontWeight: "700" }}>
                                ₹{parseFloat(userProfile?.credits || 0).toFixed(2)}
                            </span>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: "flex", gap: "12px", width: "100%" }}>
                        <Button
                            style={{
                                flex: 1,
                                height: "44px",
                                borderRadius: "8px",
                                border: "1px solid #5a31f4",
                                color: "#5a31f4",
                                fontWeight: "600",
                                fontSize: "14px",
                                background: "#fff",
                                transition: "all 0.2s ease"
                            }}
                            onClick={() => setConfirmCreditModal(false)}
                            className="credits-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            style={{
                                flex: 1,
                                height: "44px",
                                borderRadius: "8px",
                                border: "none",
                                background: "#5a31f4",
                                color: "#ffffff",
                                fontWeight: "600",
                                fontSize: "14px",
                                transition: "all 0.2s ease"
                            }}
                            onClick={handleConfirmCredits}
                            className="credits-confirm-btn"
                        >
                            Apply Credits
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default CheckoutPageContent;
